/**
 * Crear o editar un cupón.
 *
 * Una sola ventana y no un asistente por pasos: un cupón son cinco decisiones
 * cortas —el código, cuánto, a qué, hasta cuándo y cuántas veces— y verlas todas
 * juntas es lo que permite repasarlo antes de guardar.
 *
 * Los importes se escriben en unidades (12.50) y viajan en la unidad mínima,
 * como todo el dinero de esta casa. La moneda es la de la región del proyecto:
 * un descuento fijo sólo se aplica a precios de su misma moneda.
 */
import { matchesSearch } from '@webbuilder/shared-types'
import { useEffect, useMemo, useState } from 'react'
import { IconCheck, IconCopy, IconDice5, IconSearch, IconTicket, IconX } from '@tabler/icons-react'
import { createCoupon, updateCoupon, type Coupon, type CouponInput, type Offer } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'
import { useRegion } from '../../../lib/region'
import { errorMessage, minorUnitFactor, toMinorUnits } from './offerShared'
import { useToast } from '../../Toast'

/** Lo que el servidor necesita para guardar un cupón tal como está. */
export function couponInputOf(coupon: Coupon): CouponInput {
  return {
    code: coupon.code,
    description: coupon.description,
    discountType: coupon.discountType,
    percentOff: coupon.percentOff,
    amountOff: coupon.amountOff,
    duration: coupon.duration,
    durationInMonths: coupon.durationInMonths,
    appliesToAll: coupon.appliesToAll,
    offerIds: coupon.offerIds,
    maxRedemptions: coupon.maxRedemptions,
    expiresAt: coupon.expiresAt,
    minimumAmount: coupon.minimumAmount,
    active: coupon.active,
    combinesWithMember: coupon.combinesWithMember ?? false,
  }
}

/** Dónde se compra una oferta: su página, o la de reserva si tiene agenda. */
export function couponShareLink(siteOrigin: string | null, offer: Offer | undefined, code: string): string | null {
  if (!siteOrigin || !offer || offer.status !== 'active') return null
  const path = offer.booking.durationMinutes ? `/book/${encodeURIComponent(offer.slug)}` : `/checkout/buy/${offer.id}`
  return `${siteOrigin.replace(/\/+$/, '')}${path}?cupon=${encodeURIComponent(code)}`
}

/** Un código legible: sin 0/O ni 1/I, que se confunden al dictarlo. */
function randomCode(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  const bytes = crypto.getRandomValues(new Uint8Array(8))
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('')
}

/** `2026-10-01T23:59` local → ISO; vacío → null. */
function localToIso(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

function isoToLocal(value: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function unitsOf(amount: number | null, currency: string): string {
  return amount === null ? '' : String(amount / minorUnitFactor(currency))
}

export function CouponDialog({ projectId, token, coupon, offers, siteOrigin, onClose, onSaved }: {
  projectId: string
  token: string
  /** `null` para crear uno nuevo. */
  coupon: Coupon | null
  offers: Offer[]
  siteOrigin: string | null
  onClose: () => void
  onSaved: (coupon: Coupon, created: boolean) => void
}) {
  const { t } = useI18n()
  const { showToast } = useToast()
  const region = useRegion()
  const currency = coupon?.currency || region.currency
  const upper = currency.toUpperCase()

  const [code, setCode] = useState(coupon?.code ?? '')
  const [description, setDescription] = useState(coupon?.description ?? '')
  const [discountType, setDiscountType] = useState<CouponInput['discountType']>(coupon?.discountType ?? 'percent')
  const [percent, setPercent] = useState(coupon?.percentOff ? String(coupon.percentOff) : '10')
  const [amount, setAmount] = useState(unitsOf(coupon?.amountOff ?? null, currency))
  const [appliesToAll, setAppliesToAll] = useState(coupon?.appliesToAll ?? true)
  const [offerIds, setOfferIds] = useState<string[]>(coupon?.offerIds ?? [])
  const [offerQuery, setOfferQuery] = useState('')
  const [duration, setDuration] = useState<CouponInput['duration']>(coupon?.duration ?? 'once')
  const [months, setMonths] = useState(coupon?.durationInMonths ? String(coupon.durationInMonths) : '3')
  const [limitUses, setLimitUses] = useState(coupon?.maxRedemptions !== null && coupon?.maxRedemptions !== undefined)
  const [maxUses, setMaxUses] = useState(coupon?.maxRedemptions ? String(coupon.maxRedemptions) : '100')
  const [limitDate, setLimitDate] = useState(Boolean(coupon?.expiresAt))
  const [expiresAt, setExpiresAt] = useState(isoToLocal(coupon?.expiresAt ?? null))
  const [limitMinimum, setLimitMinimum] = useState(coupon?.minimumAmount !== null && coupon?.minimumAmount !== undefined)
  const [minimum, setMinimum] = useState(unitsOf(coupon?.minimumAmount ?? null, currency))
  const [active, setActive] = useState(coupon?.active ?? true)
  /* Apagado por defecto: con descuento de miembro, se aplica el que más ahorra. */
  const [combinesWithMember, setCombinesWithMember] = useState(coupon?.combinesWithMember ?? false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, saving])

  /* Las archivadas no se ofrecen para cupones nuevos, pero sí se enseñan si el
     cupón ya las tenía: quitarlas en silencio cambiaría el cupón al guardar. */
  const selectable = useMemo(() => offers.filter((offer) => offer.status !== 'archived' || offerIds.includes(offer.id)), [offers, offerIds])
  const filteredOffers = selectable.filter((offer) => matchesSearch(offer.title, offerQuery))
  /* Sólo tiene sentido hablar de meses si alguna de las ofertas se cobra cada
     cierto tiempo; si no, el descuento es de un único pago y basta. */
  const hasRecurring = (appliesToAll ? selectable : selectable.filter((offer) => offerIds.includes(offer.id)))
    .some((offer) => offer.prices.some((price) => price.active && price.interval !== 'one_time'))

  const normalizedCode = code.trim().toUpperCase().replace(/\s+/g, '')
  const codeValid = /^[A-Z0-9-]{3,40}$/.test(normalizedCode)
  const percentValue = Number(percent)
  const amountMinor = toMinorUnits(amount, currency)
  const minimumMinor = toMinorUnits(minimum, currency)
  const maxUsesValue = Number(maxUses)
  const monthsValue = Number(months)
  const expiresIso = limitDate ? localToIso(expiresAt) : null

  const problem = !codeValid
    ? t('coupons.form.errorCode')
    : discountType === 'percent' && !(Number.isInteger(percentValue) && percentValue >= 1 && percentValue <= 100)
      ? t('coupons.form.errorPercent')
      : discountType === 'amount' && !(amountMinor && amountMinor > 0)
        ? t('coupons.form.errorAmount')
        : !appliesToAll && offerIds.length === 0
          ? t('coupons.form.errorOffers')
          : hasRecurring && duration === 'repeating' && !(Number.isInteger(monthsValue) && monthsValue >= 1 && monthsValue <= 36)
            ? t('coupons.form.errorMonths')
            : limitUses && !(Number.isInteger(maxUsesValue) && maxUsesValue >= 1)
              ? t('coupons.form.errorUses')
              : limitDate && (!expiresIso || (!coupon && new Date(expiresIso).getTime() <= Date.now()))
                ? t('coupons.form.errorDate')
                : limitMinimum && !(minimumMinor && minimumMinor > 0)
                  ? t('coupons.form.errorMinimum')
                  : ''

  const save = async () => {
    if (problem || saving) {
      setError(problem)
      return
    }
    const input: CouponInput = {
      code: normalizedCode,
      description: description.trim(),
      discountType,
      percentOff: discountType === 'percent' ? percentValue : null,
      amountOff: discountType === 'amount' ? amountMinor : null,
      duration: hasRecurring ? duration : 'once',
      durationInMonths: hasRecurring && duration === 'repeating' ? monthsValue : null,
      appliesToAll,
      offerIds: appliesToAll ? [] : offerIds,
      maxRedemptions: limitUses ? maxUsesValue : null,
      expiresAt: expiresIso,
      minimumAmount: limitMinimum ? minimumMinor : null,
      active,
      combinesWithMember,
    }
    setSaving(true)
    setError('')
    try {
      const saved = coupon
        ? await updateCoupon(projectId, coupon.id, input, token)
        : await createCoupon(projectId, input, token)
      onSaved(saved, !coupon)
    } catch (failure) {
      setError(errorMessage(failure))
      setSaving(false)
    }
  }

  const toggleOffer = (id: string) => {
    setOfferIds((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id])
  }

  const shareLink = coupon && !appliesToAll && offerIds.length === 1
    ? couponShareLink(siteOrigin, offers.find((offer) => offer.id === offerIds[0]), coupon.code)
    : null

  const kindLabel = (offer: Offer) => offer.booking.durationMinutes
    ? t('coupons.form.kindService')
    : offer.type === 'digital' ? t('coupons.form.kindDigital') : t('coupons.form.kindOffer')

  return (
    <div className="dash-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget && !saving) onClose() }}>
      <section className="dash-modal dash-coupon-modal" role="dialog" aria-modal="true" aria-label={coupon ? t('coupons.form.editTitle') : t('coupons.form.newTitle')}>
        <header className="dash-coupon-modal-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true"><IconTicket size={18} /></span>
          <div>
            <h2>{coupon ? t('coupons.form.editTitle') : t('coupons.form.newTitle')}</h2>
            <p>{t('coupons.form.lead')}</p>
          </div>
          <button type="button" className="dash-icon-btn" aria-label={t('coupons.form.close')} disabled={saving} onClick={onClose}><IconX size={16} /></button>
        </header>

        <div className="dash-coupon-modal-body">
          {/* ── El código ── */}
          <div className="dash-coupon-block">
            <label className="dash-field">
              <span>{t('coupons.form.code')}</span>
              <div className="dash-coupon-code-row">
                <input
                  value={code}
                  maxLength={40}
                  autoFocus={!coupon}
                  placeholder={t('coupons.form.codePlaceholder')}
                  onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, ''))}
                />
                <button type="button" className="dash-secondary-btn" onClick={() => setCode(randomCode())}>
                  <IconDice5 size={14} /> {t('coupons.form.generate')}
                </button>
              </div>
              <small className="dash-coupon-hint">{t('coupons.form.codeHint')}</small>
            </label>
            <label className="dash-field">
              <span>{t('coupons.form.description')}</span>
              <input value={description} maxLength={200} placeholder={t('coupons.form.descriptionPlaceholder')} onChange={(event) => setDescription(event.target.value)} />
            </label>
          </div>

          {/* ── Cuánto ── */}
          <div className="dash-coupon-block">
            <span className="dash-coupon-label">{t('coupons.form.discount')}</span>
            <div className={`dash-svc-segmented${discountType === 'amount' ? ' is-second' : ''}`} role="radiogroup">
              <button type="button" role="radio" aria-checked={discountType === 'percent'} className={discountType === 'percent' ? 'is-on' : ''} onClick={() => setDiscountType('percent')}>
                {t('coupons.form.percent')}
              </button>
              <button type="button" role="radio" aria-checked={discountType === 'amount'} className={discountType === 'amount' ? 'is-on' : ''} onClick={() => setDiscountType('amount')}>
                {t('coupons.form.amount')}
              </button>
            </div>
            <div className="dash-coupon-inline">
              {discountType === 'percent' ? (
                <input type="number" min={1} max={100} step={1} value={percent} onChange={(event) => setPercent(event.target.value)} />
              ) : (
                <input type="number" min={0} step="0.01" value={amount} placeholder="0.00" onChange={(event) => setAmount(event.target.value)} />
              )}
              <em>{discountType === 'percent' ? '%' : upper}</em>
            </div>
          </div>

          {/* ── A qué ── */}
          <div className="dash-coupon-block">
            <span className="dash-coupon-label">{t('coupons.form.appliesTo')}</span>
            <div className={`dash-svc-segmented${appliesToAll ? '' : ' is-second'}`} role="radiogroup">
              <button type="button" role="radio" aria-checked={appliesToAll} className={appliesToAll ? 'is-on' : ''} onClick={() => setAppliesToAll(true)}>
                {t('coupons.form.allOffers')}
              </button>
              <button type="button" role="radio" aria-checked={!appliesToAll} className={!appliesToAll ? 'is-on' : ''} onClick={() => setAppliesToAll(false)}>
                {t('coupons.form.someOffers')}
              </button>
            </div>
            {appliesToAll ? (
              <small className="dash-coupon-hint">{t('coupons.form.allOffersHint')}</small>
            ) : (
              <div className="dash-coupon-offers">
                <div className="dash-coupon-offers-search">
                  <IconSearch size={14} aria-hidden="true" />
                  <input value={offerQuery} placeholder={t('coupons.form.searchOffers')} onChange={(event) => setOfferQuery(event.target.value)} />
                  <span>{t('coupons.form.selectedCount', { count: offerIds.length })}</span>
                </div>
                <div className="dash-coupon-offers-list">
                  {filteredOffers.length === 0 ? (
                    <p className="dash-coupon-hint">{selectable.length === 0 ? t('coupons.form.noOffers') : t('coupons.form.noOfferMatches')}</p>
                  ) : filteredOffers.map((offer) => {
                    const checked = offerIds.includes(offer.id)
                    return (
                      <label key={offer.id} className={`dash-coupon-offer${checked ? ' is-on' : ''}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleOffer(offer.id)} />
                        <span className="dash-coupon-offer-copy">
                          <strong>{offer.title || t('coupons.form.untitled')}</strong>
                          <small>
                            {kindLabel(offer)}
                            {offer.status !== 'active' ? ` · ${offer.status === 'draft' ? t('coupons.form.draft') : t('coupons.form.archived')}` : ''}
                          </small>
                        </span>
                        {checked ? <IconCheck size={14} aria-hidden="true" /> : null}
                      </label>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── En suscripciones ── */}
          {hasRecurring ? (
            <div className="dash-coupon-block">
              <span className="dash-coupon-label">{t('coupons.form.duration')}</span>
              <small className="dash-coupon-hint">{t('coupons.form.durationHint')}</small>
              <div className="dash-coupon-choices">
                {(['once', 'repeating', 'forever'] as const).map((value) => (
                  <label key={value} className={duration === value ? 'is-on' : ''}>
                    <input type="radio" name="coupon-duration" checked={duration === value} onChange={() => setDuration(value)} />
                    <span>{t(`coupons.form.duration.${value}`)}</span>
                  </label>
                ))}
              </div>
              {duration === 'repeating' ? (
                <div className="dash-coupon-inline">
                  <input type="number" min={1} max={36} step={1} value={months} onChange={(event) => setMonths(event.target.value)} />
                  <em>{t('coupons.form.months')}</em>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* ── Límites ── */}
          <div className="dash-coupon-block">
            <span className="dash-coupon-label">{t('coupons.form.limits')}</span>
            <div className="dash-coupon-limit">
              <label className="dash-coupon-check">
                <input type="checkbox" checked={limitUses} onChange={(event) => setLimitUses(event.target.checked)} />
                <span>{t('coupons.form.limitUses')}</span>
              </label>
              {limitUses ? (
                <div className="dash-coupon-inline">
                  <input type="number" min={1} step={1} value={maxUses} onChange={(event) => setMaxUses(event.target.value)} />
                  <em>{t('coupons.form.uses')}</em>
                </div>
              ) : null}
            </div>
            <div className="dash-coupon-limit">
              <label className="dash-coupon-check">
                <input type="checkbox" checked={limitDate} onChange={(event) => {
                  setLimitDate(event.target.checked)
                  /* Con una fecha propuesta —dentro de 30 días, al final del
                     día— y no vacío: el campo vacío del navegador enseña
                     «dd/mm/aaaa --:--» a medio pintar y obliga a rellenar cinco
                     casillas para algo que casi siempre es «a fin de mes». */
                  if (event.target.checked && !expiresAt) {
                    const proposal = new Date(Date.now() + 30 * 86_400_000)
                    proposal.setHours(23, 59, 0, 0)
                    setExpiresAt(isoToLocal(proposal.toISOString()))
                  }
                }} />
                <span>{t('coupons.form.limitDate')}</span>
              </label>
              {limitDate ? (
                <div className="dash-coupon-inline">
                  <input type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
                </div>
              ) : null}
            </div>
            <div className="dash-coupon-limit">
              <label className="dash-coupon-check">
                <input type="checkbox" checked={limitMinimum} onChange={(event) => setLimitMinimum(event.target.checked)} />
                <span>{t('coupons.form.limitMinimum')}</span>
              </label>
              {limitMinimum ? (
                <div className="dash-coupon-inline">
                  <input type="number" min={0} step="0.01" value={minimum} placeholder="0.00" onChange={(event) => setMinimum(event.target.value)} />
                  <em>{upper}</em>
                </div>
              ) : null}
            </div>
          </div>

          {/* ── Con el descuento de miembro ── */}
          <div className="dash-coupon-block dash-coupon-active-row">
            <div>
              <span className="dash-coupon-label">{t('coupons.form.combinesWithMember')}</span>
              <small className="dash-coupon-hint">{t(combinesWithMember ? 'coupons.form.combinesOn' : 'coupons.form.combinesOff')}</small>
            </div>
            <button
              type="button"
              className={`dash-general-toggle${combinesWithMember ? ' is-on' : ''}`}
              role="switch"
              aria-checked={combinesWithMember}
              aria-label={t('coupons.form.combinesWithMember')}
              onClick={() => setCombinesWithMember((value) => !value)}
            >
              <span />
            </button>
          </div>

          {/* ── Estado ── */}
          <div className="dash-coupon-block dash-coupon-active-row">
            <div>
              <span className="dash-coupon-label">{t('coupons.form.active')}</span>
              <small className="dash-coupon-hint">{t('coupons.form.activeHint')}</small>
            </div>
            <button
              type="button"
              className={`dash-general-toggle${active ? ' is-on' : ''}`}
              role="switch"
              aria-checked={active}
              aria-label={t('coupons.form.active')}
              onClick={() => setActive((value) => !value)}
            >
              <span />
            </button>
          </div>

          {shareLink ? (
            <div className="dash-coupon-block dash-coupon-share">
              <span className="dash-coupon-label">{t('coupons.form.shareLink')}</span>
              <div className="dash-coupon-code-row">
                <input readOnly value={shareLink} onFocus={(event) => event.currentTarget.select()} />
                <button type="button" className="dash-secondary-btn" onClick={() => { void navigator.clipboard?.writeText(shareLink); showToast(t('coupons.toast.linkCopied')) }}>
                  <IconCopy size={14} /> {t('coupons.form.copy')}
                </button>
              </div>
            </div>
          ) : null}

          {coupon && coupon.uses > 0 ? (
            <small className="dash-coupon-hint">{t('coupons.form.editWarning', { count: coupon.uses })}</small>
          ) : null}
        </div>

        {error ? <p className="dash-coupon-error" role="alert">{error}</p> : null}

        <footer>
          <button type="button" className="dash-secondary-btn" disabled={saving} onClick={onClose}>{t('coupons.form.cancel')}</button>
          <button type="button" className="dash-primary-btn" disabled={saving} onClick={() => { void save() }}>
            {saving ? t('coupons.form.saving') : coupon ? t('coupons.form.save') : t('coupons.form.create')}
          </button>
        </footer>
      </section>
    </div>
  )
}
